import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChatMessage } from '@/components/chat/ChatMessage';
import { useAppDispatch, useAppSelector } from '@/app/hooks';

import { addMessage, selectChatMessages, selectIsChatLoading, sendMessage } from '@/features/chat/chatSlice';



export const ChatPanel = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectChatMessages);
  const isLoading = useAppSelector(selectIsChatLoading);
  
  const [input, setInput] = useState('');
  const [suggestedActions, setSuggestedActions] = useState<string[] | undefined>(undefined);
  const showInputForm = true; // We always want to show the input form for now.

  // Update suggestedActions whenever messages change
  useEffect(() => {
    const lastAiMessage = [...messages].reverse().find(m => m.author === 'ai');
    setSuggestedActions(lastAiMessage?.suggestedActions);
  }, [messages]);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (messages.length === 0) {
      dispatch(
        addMessage({
          author: 'ai',
          content: t('chat.initialMessage'),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll and focus input when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    inputRef.current?.focus();
  }, [messages]);

  const handleSendMessage = (e: React.FormEvent, messageOverride?: string) => {
    e.preventDefault();
    const contentToSend = messageOverride || input;
    if (contentToSend.trim() === '' || isLoading) return;

    // Get current order items from Redux
  
    dispatch(sendMessage(contentToSend));

    if (!messageOverride) {
      setInput('');
    }
  };



  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      <ScrollArea className="flex-grow p-4" ref={scrollAreaRef}>
        <div className="space-y-2">
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      <div className="p-4 border-t dark:border-gray-700">


        {suggestedActions && suggestedActions.length > 0 && (
          <div className="border-b dark:border-gray-700 pb-4 mb-4">
            <div className="flex justify-center items-center flex-wrap gap-2">
              {suggestedActions.map((action: string, index: number) => (
                <Button
                  key={index}
                  onClick={(e) => handleSendMessage(e, action)}
                  variant="outline"
                  size="sm"
                  disabled={isLoading}
                >
                  {action}
                </Button>
              ))}
            </div>
          </div>
        )}

        {showInputForm && (
          <form
            onSubmit={(e) => handleSendMessage(e)}
            className="flex items-center space-x-2"
          >
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chat.placeholder', 'Type your message...')}
              className="flex-1"
              disabled={isLoading}
            />
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? t('chat.sending', 'Sending...')
                : t('chat.send', 'Send')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
